import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Moon, Sun, Home, Phone, FileText, Shield, Download, HelpCircle, LifeBuoy, Newspaper, Heart } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { preloadOnInteraction } from '../utils/preloadRoutes';
import { OctopusLogo } from './OctopusLogo';

interface HeaderProps {
  onNavigate?: (page: 'home' | 'about' | 'privacy' | 'terms' | 'contact' | 'error' | 'faq' | 'support' | 'blog' | 'blogPost') => void;
  currentPage?: string;
}

const mobileNavItems = [
  { 
    label: 'Home', 
    icon: Home, 
    action: 'home' as const,
    description: 'Back to homepage'
  },
  { 
    label: 'About', 
    icon: Heart, 
    action: 'about' as const,
    description: 'Learn about our mission'
  },
  { 
    label: 'Features', 
    icon: FileText, 
    action: 'features' as const,
    description: 'Explore our features'
  },
  { 
    label: 'Pricing', 
    icon: FileText, 
    action: 'pricing' as const,
    description: 'View pricing plans'
  },
  { 
    label: 'FAQ', 
    icon: HelpCircle, 
    action: 'faq' as const,
    description: 'Frequently asked questions'
  },
  { 
    label: 'Support', 
    icon: LifeBuoy, 
    action: 'support' as const,
    description: 'Get help and support'
  },
  { 
    label: 'Blog', 
    icon: Newspaper, 
    action: 'blog' as const,
    description: 'Read our latest articles'
  },
  { 
    label: 'Contact', 
    icon: Phone, 
    action: 'contact' as const,
    description: 'Get in touch with us'
  }
];

export const Header: React.FC<HeaderProps> = ({ onNavigate, currentPage }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const prefersReducedMotion = useReducedMotion();

  const animationProps = prefersReducedMotion ? {} : {
    initial: { opacity: 0, y: -20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const handleNavigation = (page: 'home' | 'about' | 'privacy' | 'terms' | 'contact' | 'error' | 'faq' | 'support' | 'blog' | 'blogPost') => {
    onNavigate?.(page);
    setIsMenuOpen(false);
  };

  const handleDownloadApp = () => {
    if (currentPage === 'home') {
      const downloadSection = document.getElementById('download');
      if (downloadSection) {
        downloadSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      handleNavigation('home');
      // After navigation, scroll to download section
      setTimeout(() => {
        const downloadSection = document.getElementById('download');
        if (downloadSection) {
          downloadSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    setIsMenuOpen(false);
  };

  const handleMobileNavAction = (action: string) => {
    switch (action) {
      case 'home':
        handleNavigation('home');
        break;
      case 'about':
        handleNavigation('about');
        break;
      case 'features':
        if (currentPage === 'home') {
          document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
        } else {
          handleNavigation('home');
        }
        break;
      case 'pricing':
        if (currentPage === 'home') {
          document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
        } else {
          handleNavigation('home');
        }
        break;
      case 'faq':
        handleNavigation('faq');
        break;
      case 'support':
        handleNavigation('support');
        break;
      case 'blog':
        handleNavigation('blog');
        break;
      case 'contact':
        handleNavigation('contact');
        break;
      case 'download':
        handleDownloadApp();
        break;
      default:
        setIsMenuOpen(false);
    }
  };

  return (
    <>
      <motion.header 
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/10 dark:bg-gray-900/10 border-b border-white/20 dark:border-gray-700/20"
        {...animationProps}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 lg:h-18">
            {/* Logo */}
            <motion.button 
              onClick={() => handleNavigation('home')}
              className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-shrink-0"
              whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <OctopusLogo size="sm" showGlow={true} className="sm:w-8 sm:h-8" />
              <span className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white truncate">
                Octify
              </span>
            </motion.button>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              {[
                { label: 'About', action: () => handleNavigation('about'), preload: 'about' },
                { label: 'Features', action: () => currentPage === 'home' ? document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' }) : handleNavigation('home') },
                { label: 'Pricing', action: () => currentPage === 'home' ? document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }) : handleNavigation('home') },
                { label: 'FAQ', action: () => handleNavigation('faq'), preload: 'faq' },
                { label: 'Support', action: () => handleNavigation('support'), preload: 'support' },
                { label: 'Blog', action: () => handleNavigation('blog'), preload: 'blog' },
                { label: 'Contact', action: () => handleNavigation('contact'), preload: 'contact' }
              ].map((item) => (
                <motion.button
                  key={item.label}
                  onClick={item.action}
                  className="text-sm xl:text-base text-gray-700 dark:text-gray-300 hover:text-orange-500 dark:hover:text-orange-400 transition-colors duration-200 font-medium"
                  whileHover={prefersReducedMotion ? {} : { y: -2 }}
                  transition={{ duration: 0.2 }}
                  {...(item.preload ? preloadOnInteraction(item.preload) : {})}
                >
                  {item.label}
                </motion.button>
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center space-x-3 xl:space-x-4">
              <motion.button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm hover:bg-white/20 dark:hover:bg-gray-800/20 transition-colors duration-200"
                whileHover={prefersReducedMotion ? {} : { scale: 1.1 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              >
                {theme === 'light' ? (
                  <Moon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                ) : (
                  <Sun className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                )}
              </motion.button>
              <motion.button
                onClick={handleDownloadApp}
                className="px-4 xl:px-6 py-2 xl:py-2.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full font-medium hover:shadow-lg transition-all duration-200 flex items-center gap-2 text-sm xl:text-base"
                whileHover={prefersReducedMotion ? {} : { scale: 1.05, boxShadow: "0 10px 25px rgba(255, 107, 53, 0.3)" }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
              >
                <Download className="w-4 h-4" />
                <span className="hidden xl:inline">Download App</span>
                <span className="xl:hidden">Download</span>
              </motion.button>
            </div>

            {/* Mobile/Tablet Actions */}
            <div className="flex lg:hidden items-center space-x-2 sm:space-x-3">
              {/* Theme Toggle - Always visible on mobile */}
              <motion.button
                onClick={toggleTheme}
                className="p-2 sm:p-2.5 rounded-lg bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm hover:bg-white/20 dark:hover:bg-gray-800/20 transition-colors duration-200"
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              >
                {theme === 'light' ? (
                  <Moon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 dark:text-gray-300" />
                ) : (
                  <Sun className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 dark:text-gray-300" />
                )}
              </motion.button>

              {/* Download Button - Hidden on small screens, visible on tablets */}
              <motion.button
                onClick={handleDownloadApp}
                className="hidden sm:flex md:flex lg:hidden px-3 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full font-medium text-sm items-center gap-1.5"
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
              >
                <Download className="w-4 h-4" />
                <span>Download</span>
              </motion.button>

              {/* Menu Button */}
              <motion.button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 sm:p-2.5 rounded-lg bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm hover:bg-white/20 dark:hover:bg-gray-800/20 transition-colors duration-200"
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMenuOpen}
              >
                <AnimatePresence mode="wait">
                  {isMenuOpen ? (
                    <motion.div
                      key="close"
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 dark:text-gray-300" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="menu"
                      initial={{ rotate: 90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: -90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Menu className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 dark:text-gray-300" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Bottom Sheet Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setIsMenuOpen(false)}
            />

            {/* Bottom Sheet */}
            <motion.div
              className="fixed bottom-0 left-0 right-0 z-50 lg:hidden"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ 
                type: 'spring', 
                damping: 25, 
                stiffness: 300,
                duration: 0.4 
              }}
            >
              <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-t border-gray-200/50 dark:border-gray-700/50 rounded-t-3xl shadow-2xl">
                {/* Handle */}
                <div className="flex justify-center pt-3 pb-2">
                  <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
                </div>

                {/* Header */}
                <div className="px-4 sm:px-6 py-4 border-b border-gray-200/30 dark:border-gray-700/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <OctopusLogo size="sm" showGlow={true} />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Navigation
                      </h3>
                    </div>
                    <motion.button
                      onClick={() => setIsMenuOpen(false)}
                      className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                      whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                      aria-label="Close navigation menu"
                    >
                      <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </motion.button>
                  </div>
                </div>

                {/* Navigation Items */}
                <div className="px-3 sm:px-4 py-6 max-h-[60vh] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    {mobileNavItems.map((item, index) => (
                      <motion.button
                        key={item.label}
                        onClick={() => handleMobileNavAction(item.action)}
                        className="flex flex-col items-center p-3 sm:p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 group active:scale-95"
                        initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        whileHover={prefersReducedMotion ? {} : { scale: 1.02 }}
                        whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
                        {...(['about', 'faq', 'support', 'blog', 'contact'].includes(item.action) ? preloadOnInteraction(item.action) : {})}
                      >
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-orange-500 to-pink-500 rounded-xl flex items-center justify-center mb-2 sm:mb-3 group-hover:shadow-lg transition-shadow duration-200">
                          <item.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white mb-1 text-center">
                          {item.label}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 text-center leading-tight px-1">
                          {item.description}
                        </span>
                      </motion.button>
                    ))}
                  </div>

                  {/* Download App Button */}
                  <motion.button
                    className="w-full mt-6 px-6 py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-2xl font-semibold text-base sm:text-lg shadow-lg flex items-center justify-center gap-2 active:scale-95 transition-transform duration-200"
                    initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                    whileHover={prefersReducedMotion ? {} : { 
                      scale: 1.02, 
                      boxShadow: "0 20px 40px rgba(255, 107, 53, 0.3)" 
                    }}
                    whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
                    onClick={handleDownloadApp}
                  >
                    <Download className="w-5 h-5" />
                    Download App
                  </motion.button>
                </div>

                {/* Safe Area Bottom Padding */}
                <div className="h-safe-area-inset-bottom" />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};